'use client';

import { X } from 'lucide-react';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';

/**
 * Fixed top banner shown when an admin has the "Alle Kunden" toggle active.
 * Three modes make the admin context unmissable:
 *   all    — toggle on, no debitor selected       → amber, "Alle Kunden:"
 *   single — toggle on, exactly one debitor       → red,   "Cross-Login: Name (KDNR)"
 *   multi  — toggle on, two or more debitoren     → amber, "Multi Kunden: Name1, Name2, …"
 *
 * The "Beenden" button always exits the mode entirely (toggle off + selection cleared).
 */
export function ImpersonationBanner() {
  const { isAdmin, allCustomers, selectedCustomers, setAllCustomers, clearCustomers } = useAdmin();

  const mode: 'hidden' | 'all' | 'single' | 'multi' =
    !isAdmin || !allCustomers
      ? 'hidden'
      : selectedCustomers.length === 0
        ? 'all'
        : selectedCustomers.length === 1
          ? 'single'
          : 'multi';

  if (mode === 'hidden') return null;

  const handleExit = () => {
    clearCustomers();
    setAllCustomers(false);
  };

  const barClass = mode === 'single' ? 'bg-red-600' : 'bg-amber-500';
  const btnClass =
    mode === 'single'
      ? 'bg-red-700 hover:bg-red-800'
      : 'bg-amber-600 hover:bg-amber-700';
  const exitAriaLabel =
    mode === 'single' ? 'Cross-Login beenden' : '„Alle Kunden“-Modus beenden';

  const label =
    mode === 'all'
      ? 'Alle Kunden:'
      : mode === 'single'
        ? `Cross-Login: ${selectedCustomers[0].customerName} (${selectedCustomers[0].customerNumber})`
        : `Multi Kunden: ${selectedCustomers.map((c) => c.customerName).join(', ')}`;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 inset-x-0 z-50 h-10 ${barClass} text-white flex items-center justify-between px-4 md:px-6 shadow-md`}
    >
      <p className="text-sm font-medium truncate mr-4">{label}</p>
      <button
        onClick={handleExit}
        aria-label={exitAriaLabel}
        className={`shrink-0 flex items-center gap-1.5 px-3 py-1 text-xs font-semibold ${btnClass} rounded transition-colors`}
      >
        <X size={14} />
        <span className="hidden sm:inline">Beenden</span>
      </button>
    </div>
  );
}
